import { paymentProcessingApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { ContractorBatchGateway } from './ContractorBatchGateway';
import type { AddLineInput, BatchDatesInput, BatchPage, ContractorBatch, ContractorBatchStatus, ContractorPaymentSettings, UpdateLineInput } from './types';

const BASE = '/api/contractor-payment-batches';

export class HttpContractorBatchGateway implements ContractorBatchGateway {
  private readonly request = createJsonClient(paymentProcessingApiBaseUrl);

  async listBatches(status: Exclude<ContractorBatchStatus, 'Discarded'>, signal?: AbortSignal): Promise<ContractorBatch[]> {
    const page = await this.request<BatchPage>(`${BASE}?status=${encodeURIComponent(status)}&page=1&pageSize=100`, undefined, signal);
    return page.items;
  }

  getBatch(id: string, signal?: AbortSignal): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}`, undefined, signal);
  }

  createBatch(input: BatchDatesInput): Promise<ContractorBatch> {
    return this.request(BASE, { method: 'POST', body: JSON.stringify(input) });
  }

  updateDates(id: string, input: BatchDatesInput, expectedVersion: number): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ ...input, expectedVersion }) });
  }

  addLine(id: string, input: AddLineInput, expectedVersion: number): Promise<ContractorBatch> {
    // A computed amount is never sent for hours x rate: the server computes it. Only the fields of the chosen mode go over the wire.
    const body =
      input.amountMode === 'Flat'
        ? { contractorId: input.contractorId, description: input.description, amountMode: 'Flat', amount: input.amount, expectedVersion }
        : { contractorId: input.contractorId, description: input.description, amountMode: 'HoursRate', hours: input.hours, rate: input.rate, expectedVersion };
    return this.request(`${BASE}/${encodeURIComponent(id)}/lines`, { method: 'POST', body: JSON.stringify(body) });
  }

  updateLine(id: string, lineId: string, input: UpdateLineInput, expectedVersion: number): Promise<ContractorBatch> {
    const body =
      input.amountMode === 'Flat'
        ? { description: input.description, amountMode: 'Flat', amount: input.amount, expectedVersion }
        : { description: input.description, amountMode: 'HoursRate', hours: input.hours, rate: input.rate, expectedVersion };
    return this.request(`${BASE}/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  editCheckNumber(id: string, lineId: string, checkNumber: string, expectedVersion: number): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}/check-number`, {
      method: 'PUT',
      body: JSON.stringify({ checkNumber, expectedVersion }),
    });
  }

  removeLines(id: string, lineIds: string[], expectedVersion: number): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/lines/remove`, { method: 'POST', body: JSON.stringify({ lineIds, expectedVersion }) });
  }

  runReadiness(id: string, expectedVersion: number): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/readiness`, { method: 'POST', body: JSON.stringify({ expectedVersion }) });
  }

  acknowledgeWarnings(id: string, warningIds: string[], expectedVersion: number): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/warning-acknowledgement`, {
      method: 'POST',
      body: JSON.stringify({ warningIds, expectedVersion }),
    });
  }

  saveDraft(id: string, currentStep: number, expectedVersion: number): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/draft`, { method: 'POST', body: JSON.stringify({ currentStep, expectedVersion }) });
  }

  async discard(id: string, expectedVersion: number): Promise<void> {
    await this.request<unknown>(`${BASE}/${encodeURIComponent(id)}/discard`, { method: 'POST', body: JSON.stringify({ expectedVersion }) });
  }

  approve(id: string, expectedVersion: number, idempotencyKey: string): Promise<ContractorBatch> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ reviewConfirmed: true, expectedVersion }),
    });
  }

  getSettings(signal?: AbortSignal): Promise<ContractorPaymentSettings> {
    return this.request('/api/contractor-payment-settings', undefined, signal);
  }

  saveSettings(form1099ThresholdAmount: number | null, approachingPercent?: number | null): Promise<ContractorPaymentSettings> {
    // The server replaces both values, so the percent travels whenever the caller knows it.
    const body = approachingPercent === undefined ? { form1099ThresholdAmount } : { form1099ThresholdAmount, form1099ApproachingPercent: approachingPercent };
    return this.request('/api/contractor-payment-settings', { method: 'PUT', body: JSON.stringify(body) });
  }
}
